import axios from 'axios';
import toast from 'react-hot-toast';

import { API_URL, MOCK_AUTH, MOCK_USER_ROLE, ROLES } from 'config/constants';
import { buildMetadata } from './metadata';
import { retrieveClientConfiguration } from './client';

const defaultMessage = (status) => {
  switch (status) {
    case 401:
      return 'Not Authorized';
    case 403:
      return 'Insufficient permission';
    case 404:
      return 'Not Found';
    default:
      return 'Error while contacting the server';
  }
};

const dataOnly = (response) => {
  if (response.config.responseType === 'blob') {
    return response;
  } else {
    return response.data;
  }
};

export const createApiInstance = (auth, userPathOverride = null) => {
  let baseURL = API_URL;

  if (userPathOverride) {
    baseURL += `/${userPathOverride}/${auth.slug}`;
  } else if (auth?.clientId) {
    const userPath =
      auth.userRole === ROLES.ASSESSMENT_USER || auth.userRole === ROLES.EMPLOYEE_USER ? 'enduser' : 'client';
    baseURL += `/${userPath}/${auth.slug}`;
  }

  const headers = {};
  if (auth?.token) {
    headers.Authorization = `Bearer ${auth.token}`;
  }

  const instance = axios.create({
    baseURL,
    headers,
  });

  instance.interceptors.response.use(dataOnly, (error) => {
    const { config, response } = error;
    const { data, status } = response;
    let msg = (data && data.title) || defaultMessage(status);

    if (status === 401) {
      // TODO:  JWL 21 July 2022 - will need a redirect here
      //    later, however it's not defined 'where' yet.
    } else if (status === 400) {
      // errors is an object, each key is an array of strings,
      //    i.e. errors: {"firstName": ['one', 'two'], "lastName": ['one']}
      const errors = data.errors && Object.keys(data.errors).reduce((acc, key) => acc.concat(data.errors[key]), []);

      if (errors) {
        msg = `${msg}\n\n${errors.join('\n')}`;
      }
    }

    response.handlerMessage = msg;

    if (!config.withoutToast) {
      toast.error(response.handlerMessage);
    }

    return Promise.reject(error);
  });

  return instance;
};

export const HutricsAuthProvider = {
  login(username, password, clientIdOrSlug, isClientId, callback) {
    if (MOCK_AUTH) {
      callback({
        token: 'mocked-token',
        slug: isClientId ? 'acme-widgets' : clientIdOrSlug,
        cid: isClientId ? clientIdOrSlug : 'c5487018-9a95-44b5-8164-9ed4fb060c58',
        displayName: 'Ray Elenteny',
        clientName: 'Acme Widgets, Inc.',
        userRole: MOCK_USER_ROLE,
        expires: 300000,
      });
      return;
    }

    const api = createApiInstance();

    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    if (isClientId) {
      formData.append('cid', clientIdOrSlug);
    } else {
      formData.append('client_url_slug', clientIdOrSlug);
    }

    api
      .request({
        method: 'POST',
        url: `${API_URL}/enduser/login`,
        data: formData,
      })
      .then((response) => {
        let userRole = ROLES.ASSESSMENT_USER;
        if (response.data.is_client) {
          userRole = ROLES.CLIENT_USER;
        } else if (response.data.is_client_employee) {
          userRole = ROLES.EMPLOYEE_USER;
        }
        const authData = {
          token: response.data.access_token,
          clientId: response.data.cid,
          slug: response.data.client_url_slug,
          displayName: response.data.name,
          clientName: response.data.client_name,
          userId: response.data.user_id,
          userRole,
          expires: response.data.expires_in,
          metadata: buildMetadata(response.data.metadata),
        };
        retrieveClientConfiguration(authData).then(() => {
          callback(authData);
        });
      })
      .catch((reason) => {
        console.log(`error: ${reason}`);
        callback(undefined);
      });
  },

  logout(callback) {
    setTimeout(callback, 100);
  },
};
